import get from 'lodash/get';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { replace } from 'react-router-redux';
import { call, put, select } from 'redux-saga/effects';

import * as VerificationActions from '../actions';
import * as VerificationConstants from '../constants';
import * as D from '../deps';
import * as VerificationSelectors from '../selectors';
import * as VerificationServices from '../services';
import * as VerificationUtils from '../utils';

const log = D.Logger.child(
  { component: 'src/domains/payments/verification/sagas/workers/Payments' },
  true,
);

export function* redirectToDiscountVerification({ payload: { tierSlug, verificationType } }) {
  const status = yield select(VerificationSelectors.getDiscountVerificationStatus);
  if (
    status === VerificationConstants.VerificationStatus.VERIFIED ||
    (status === VerificationConstants.VerificationStatus.RE_VERIFIED &&
      verificationType === VerificationConstants.VerificationTypes.MILITARY) ||
    status === VerificationConstants.VerificationStatus.DENIED_4_YEAR
  ) {
    yield put(replace(D.Utils.Route.getPaymentPath(tierSlug, verificationType)));
  } else if (status === VerificationConstants.VerificationStatus.DOC_REVIEW) {
    const documentUploadUrl = yield select(
      VerificationSelectors.getDiscountVerificationDocumentUploadUrl,
    );
    if (documentUploadUrl) {
      window.location = documentUploadUrl;
    } else {
      yield call(D.PaymentsHelpers.handleGenericSubscriptionFailureWithMessage, {
        errorString: 'Missing documentUploadUrl',
        redirectPath: D.Utils.Route.getUpgradePath(),
      });
    }
  } else {
    const discountVerificationPath = D.Utils.Route.getDiscountVerificationPath(
      tierSlug,
      verificationType,
    );
    if (discountVerificationPath) {
      yield put(replace(discountVerificationPath));
    }
  }
}

export function* onSubmitDiscountVerificationSuccess() {
  const discountType = yield select(D.PaymentsSelectors.getSelectedDiscountType);
  const status = yield select(VerificationSelectors.getDiscountVerificationStatus);
  if (
    status === VerificationConstants.VerificationStatus.RE_VERIFIED &&
    discountType === D.PaymentsConstants.discountTypes.STUDENT
  ) {
    yield call(D.PaymentsHelpers.logUpgradeResult, {
      action: D.getInDomain('stats.constants.StatsActions.VIEW'),
      page_view: D.PaymentsConstants.Analytics.REVERIFY_SUCCESS,
      view_mode: D.getInDomain('stats.constants.ViewModes.UPGRADE_STUDENT'),
    });
    yield put(replace(D.Utils.Route.getStudentReverificationCongratulationsPath()));
    return;
  }
  const tier = yield select(D.PaymentsSelectors.getSelectedTier);
  const tierSlug = D.PaymentsUtils.getTierSlugFromTier(tier);
  const verificationType = VerificationUtils.getVerificationTypeFromDiscountType(discountType);
  yield put(
    VerificationActions.redirectToDiscountVerification({
      tierSlug,
      verificationType,
    }),
  );
}

export function* onSubmitDiscountVerificationFail(action) {
  const errorType = get(action, 'error.errorType');
  if (errorType === D.ApiErrorTypes.INVALID_REQUEST_USER_ALREADY_VERIFIED) {
    try {
      const discountType = get(action, 'error.action.content.discountType');
      yield call(D.putAndWait, VerificationActions.getDiscountVerificationInfo({ discountType }));
    } catch (error) {
      yield call(
        handleGenericSubscriptionFailureWithModal,
        error,
        'Failed to refresh discount verification info',
      );
      return;
    }

    yield call(onSubmitDiscountVerificationSuccess);
  }
}

export function* handleGenericSubscriptionFailureWithModal(error, defaultMessage) {
  const errorMessage = get(error, 'message', defaultMessage);
  log.error(errorMessage);

  const modalMessage = (
    <FormattedMessage {...D.PaymentsConstants.Messages.somethingHasGoneWrongRefreshRetry} />
  );
  yield put(D.Actions.Modal.openSomethingGoneWrongModal(modalMessage));
}

export function* discountOrgSearchAutocomplete({ payload }) {
  yield call(VerificationServices.discountOrgSearchAutocomplete, payload);
}

export function* getDiscountVerificationInfo({ payload }) {
  yield put(VerificationActions.clearDiscountVerificationInfo());
  yield call(D.PaymentsServices.getDiscountVerificationInfo, payload);
}

export function* submitDiscountVerification({ payload }) {
  try {
    yield call(VerificationServices.submitDiscountVerification, payload);
  } catch (error) {
    const { message, errorType } = error;
    log.error(
      `Error calling Discount Verification Service: ${errorType}, message: ${message}, error: ${JSON.stringify(
        error,
        null,
        2,
      )}`,
    );
  }
}
